import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useReactiveVar } from '@apollo/client';
import { shoppingCartOffersVar } from '../../apollo/reactive-variables/shopping-cart';
import { GridEditDateCell } from '.';
import { UNTIL_FIFTEEN_DAYS } from '../../constants';
import dayjs from '../../config/dayjs';
import useGetCountryFromUrl from '../../hooks/useGetCountryFromUrl';

const DateToPayEditCell = ({ params, selectedOfferIds }) => {
  const currentOffers = useReactiveVar(shoppingCartOffersVar);
  const country = useGetCountryFromUrl();

  const dateValue = useMemo(() => {
    const selectedOffer = currentOffers.find(
      ({ invoiceId }) => params.id === invoiceId,
    );
    return selectedOffer?.dateToPay || null;
  }, [currentOffers]);

  const onChange = (row, newValue) => {
    shoppingCartOffersVar([
      { invoiceId: row.id, dateToPay: newValue },
      ...currentOffers.filter((offer) => offer.invoiceId !== row.id),
    ]);
  };

  const onChangeAll = (newValue) => {
    if (selectedOfferIds?.length > 0) {
      const updatedOffers = currentOffers.map((offer) => {
        if (selectedOfferIds.includes(offer.invoiceId)) {
          return { ...offer, dateToPay: newValue };
        }
        return offer;
      });
      shoppingCartOffersVar(updatedOffers);
    } else {
      const updatedOffers = currentOffers.map((offer) => ({
        ...offer,
        dateToPay: newValue,
      }));
      shoppingCartOffersVar(updatedOffers);
    }
  };

  const momentDateExpiration = params.row?.dateExpiration
    ? dayjs(params.row?.dateExpiration)
    : null;

  const momentDateIssued = params.row?.dateIssued
    ? dayjs(params.row?.dateIssued)
    : null;

  const debtorAveragePaymentDays = useMemo(() => {
    const days = params.row?.receiver?.invoicesActualPaymentTerm;
    if (days === undefined) return null;
    if (days < 15) return null;
    return days;
  }, [params]);

  // Country-specific default funding days for maximum date calculation
  const getDefaultFundingDays = () => {
    switch (country?.name) {
      case 'Mexico':
        return 90;
      case 'Chile':
      default:
        return 60;
    }
  };

  const maxDate = useMemo(() => {
    const fundingDays = params.row.offer?.fundingDaysAfterExpiration || getDefaultFundingDays();

    if (country?.name === 'Mexico') {
      return momentDateIssued
        ? momentDateIssued.clone().add(fundingDays, 'days')
        : null;
    }

    return momentDateExpiration
      ? momentDateExpiration.clone().add(fundingDays, 'days')
      : null;
  }, [
    country?.name,
    momentDateExpiration,
    momentDateIssued,
    params.row.offer?.fundingDaysAfterExpiration,
  ]);

  return (
    <GridEditDateCell
      {...params}
      debtorAveragePaymentDays={debtorAveragePaymentDays}
      value={dateValue}
      onChange={onChange}
      onChangeAll={onChangeAll}
      minDate={UNTIL_FIFTEEN_DAYS}
      maxDate={maxDate}
    />
  );
};

DateToPayEditCell.propTypes = {
  params: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    row: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      dateExpiration: PropTypes.objectOf(dayjs),
      dateIssued: PropTypes.objectOf(dayjs),
      offer: PropTypes.shape({
        fundingDaysAfterExpiration: PropTypes.number,
      }),
      receiver: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        invoicesActualPaymentTerm: PropTypes.number,
      }),
    }),
  }).isRequired,
  selectedOfferIds: PropTypes.arrayOf(PropTypes.string),
};

DateToPayEditCell.defaultProps = {
  selectedOfferIds: [],
};

export default DateToPayEditCell;
